import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { listFiles, type FileView } from '../files/api';
import { composePost, editPost, NETWORK_LIMITS, type SocialAccount, type SocialPost } from './api';

/**
 * Writing a post.
 *
 * The account comes first because it decides everything else: how long the text may be, whether an
 * image is required, whether a person will have to approve it. The counter turns red before the
 * API says no. Images come from the tenant's own files — the same library the rest of the product
 * uses — rather than a second upload path that would need its own quota and validation.
 */
export function PostEditor({
  accounts,
  post,
  defaultDate,
  onClose,
  onSaved,
}: {
  accounts: SocialAccount[];
  post?: SocialPost;
  defaultDate?: string;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);

  const [accountId, setAccountId] = useState(post?.accountId ?? accounts[0]?.id ?? '');
  const [body, setBody] = useState(post?.body ?? '');
  const [linkUrl, setLinkUrl] = useState(post?.linkUrl ?? '');
  const [media, setMedia] = useState<string[]>(post?.mediaFileIds ?? []);
  const [date, setDate] = useState(
    post?.scheduledAt ? localDate(post.scheduledAt) : (defaultDate ?? ''),
  );
  const [time, setTime] = useState(post?.scheduledAt ? localTime(post.scheduledAt) : '09:00');
  const [pickingImages, setPickingImages] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
  }, []);

  const account =
    accounts.find((item) => item.id === accountId) ??
    (post ? { network: post.network, requiresApproval: false } : undefined);
  const limits = account ? NETWORK_LIMITS[account.network] : NETWORK_LIMITS.LOCAL;
  const tooLong = body.length > limits.maxChars;
  const needsMedia = limits.requiresMedia && media.length === 0;

  const save = useMutation({
    mutationFn: () => {
      const schedule = date ? { date, time: time || '09:00' } : null;
      const payload = {
        body: body.trim(),
        mediaFileIds: media,
        linkUrl: linkUrl.trim() || null,
        schedule,
      };

      return post ? editPost(post.id, payload) : composePost({ accountId, ...payload });
    },
    onSuccess: onSaved,
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const canSave =
    body.trim() !== '' && !tooLong && !needsMedia && (post || accountId) && !save.isPending;

  return (
    <dialog ref={dialog} className="confirm confirm--wide" onCancel={onClose} onClose={onClose}>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <h2>{post ? t('social.editPost') : t('social.newPost')}</h2>

        {!post && (
          <label className="stack__field">
            <span>{t('social.account')}</span>
            <select
              className="select"
              value={accountId}
              onChange={(event) => setAccountId(event.target.value)}
            >
              {accounts.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name} · {t(`social.network.${item.network}`)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="stack__field">
          <span>
            {t('social.text')}
            <span className={`social-counter${tooLong ? ' social-counter--over' : ''}`}>
              {` ${body.length} / ${limits.maxChars}`}
            </span>
          </span>
          <textarea
            rows={6}
            value={body}
            autoFocus
            placeholder={t('social.textPlaceholder')}
            onChange={(event) => setBody(event.target.value)}
          />
        </label>

        <div className="stack__field">
          <span>
            {t('social.images')}
            {limits.requiresMedia && <span className="required"> *</span>}
          </span>
          <div className="cell-inline">
            {media.length > 0 && (
              <span className="muted small">
                {media.length} {t('social.selected')}
              </span>
            )}
            <button
              type="button"
              className="button button--quiet button--compact"
              onClick={() => setPickingImages(true)}
            >
              {media.length > 0 ? t('social.changeImages') : t('social.pickImages')}
            </button>
            {media.length > 0 && (
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => setMedia([])}
              >
                {t('social.clearImages')}
              </button>
            )}
          </div>
          {needsMedia && <span className="muted small">{t('social.needsMedia')}</span>}
        </div>

        <label className="stack__field">
          <span>{t('social.link')}</span>
          <input
            type="url"
            value={linkUrl}
            placeholder="https://"
            onChange={(event) => setLinkUrl(event.target.value)}
          />
        </label>

        <div className="form-grid">
          <label className="stack__field">
            <span>{t('social.date')}</span>
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          <label className="stack__field">
            <span>{t('social.time')}</span>
            <input
              type="time"
              value={time}
              disabled={!date}
              onChange={(event) => setTime(event.target.value)}
            />
          </label>
        </div>

        <p className="muted small">
          {!date
            ? t('social.willBeDraft')
            : account && 'requiresApproval' in account && account.requiresApproval
              ? t('social.willWaitApproval')
              : t('social.willBeScheduled')}
        </p>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>
          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={!canSave}
          >
            {save.isPending
              ? t('work.creating')
              : date
                ? t('social.schedule')
                : t('social.saveDraft')}
          </button>
        </div>
      </form>

      {pickingImages && (
        <ImagePicker
          selected={media}
          onDone={(ids) => {
            setMedia(ids);
            setPickingImages(false);
          }}
          onClose={() => setPickingImages(false)}
        />
      )}
    </dialog>
  );
}

/** Images from the library. Only images: a PDF on Instagram is a 422 with extra steps. */
function ImagePicker({
  selected,
  onDone,
  onClose,
}: {
  selected: string[];
  onDone: (ids: string[]) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<string[]>(selected);

  const files = useQuery({
    queryKey: ['social-images', search],
    queryFn: () => listFiles({ mimePrefix: 'image/', ...(search ? { search } : {}) }),
  });

  const toggle = (file: FileView): void => {
    setChosen((current) =>
      current.includes(file.id)
        ? current.filter((id) => id !== file.id)
        : current.length >= 10
          ? current
          : [...current, file.id],
    );
  };

  return (
    <div className="picker">
      <div className="picker__head">
        <input
          value={search}
          placeholder={t('files.searchPlaceholder')}
          onChange={(event) => setSearch(event.target.value)}
        />
        <span className="muted small">{chosen.length} / 10</span>
      </div>

      <div className="picker__list">
        {files.isLoading ? (
          <span className="muted small">{t('social.loading')}</span>
        ) : (files.data?.files ?? []).length === 0 ? (
          <span className="muted small">{t('social.noImages')}</span>
        ) : (
          (files.data?.files ?? []).map((file) => (
            <label
              key={file.id}
              className={`picker__item${chosen.includes(file.id) ? ' picker__item--on' : ''}`}
            >
              <input
                type="checkbox"
                checked={chosen.includes(file.id)}
                onChange={() => toggle(file)}
              />
              <span className="picker__name">{file.name}</span>
              <span className="muted small">{file.mimeType.replace('image/', '')}</span>
            </label>
          ))
        )}
      </div>

      <div className="confirm__actions">
        <button type="button" className="button button--quiet button--compact" onClick={onClose}>
          {t('confirm.cancel')}
        </button>
        <button
          type="button"
          className="button button--primary button--compact"
          onClick={() => onDone(chosen)}
        >
          {t('social.useImages')}
        </button>
      </div>
    </div>
  );
}

function localDate(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function localTime(iso: string): string {
  const date = new Date(iso);
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
